/* vnk-bg-products.js
   Production carousel for visa-floater background (rotates single product with zoom-out + details)
   - GraphQL first (Storefront API) using window.__vnk_storefront_token__
   - Fallback REST /collections/<handle>/products.json
   - Injects inside .vnk-panel-bg and respects existing z-index stacking
*/

(function () {
  // -------- CONFIG --------
  const COLLECTION_HANDLE = 'best-sellers'; // change if needed
  const MAX_PRODUCTS = 12;
  const ROTATE_MS = 6000;   // how long each product is visible (ms)
  const FADE_MS = 700;      // fade transition time (ms)
  const INITIAL_SCALE = 1.10; // starting scale for zoom-out
  // ------------------------

  const sfToken = window.__vnk_storefront_token__ || '';
  const gqlEndpoint = '/api/2023-10/graphql.json';
  const restEndpoint = `/collections/${encodeURIComponent(COLLECTION_HANDLE)}/products.json?limit=${MAX_PRODUCTS}`;

  // robust extractor
  function extractRawUrl(bg) {
    if (!bg) return '';
    let url = bg.toString().trim();
    const urlRe = /^url\(["']?(.+?)["']?\)$/i;
    while (urlRe.test(url)) url = url.replace(urlRe, '$1').trim();
    const wrapperRe = /^https?:\/\/[^/]+\/url\(["']?(.+?)["']?\)$/i;
    while (wrapperRe.test(url)) url = url.replace(wrapperRe, '$1').trim();
    try { url = decodeURIComponent(url); } catch (e) {}
    url = url.replace(/^["']|["']$/g, '').trim();
    return url;
  }

  // formatting helpers
  function formatINR(v) {
    const n = Number(v || 0);
    return isNaN(n) ? '₹' + v : '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }
  function computeDiscount(ca, p) {
    const C = parseFloat(ca || 0);
    const P = parseFloat(p || 0);
    if (!C || !P || C <= P) return 0;
    return Math.round(((C - P) / C) * 100);
  }

  // inject scoped CSS (idempotent)
  function injectStyles() {
    if (document.getElementById('vnk-bg-products-styles')) return;
    const css = `
/* vnk-bg-products (scoped) */
.vnk-bg-carousel { position: absolute; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; }
.vnk-bg-carousel-track { position: absolute; inset: 0; }
.vnk-bg-slide {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100%; height: 100%;
  background-size: cover; background-position: center;
  opacity: 0;
  transform: scale(${INITIAL_SCALE});
  transition: opacity ${FADE_MS}ms ease, transform ${ROTATE_MS}ms linear;
  will-change: opacity, transform;
}
.vnk-bg-slide.vnk-active { opacity: 1; transform: scale(1); }

/* dim overlay sits above the slides but below the card */
.vnk-bg-dim {
  position:absolute; inset:0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(0,0,0,0.12) 40%, rgba(0,0,0,0.6) 100%);
  pointer-events:none;
}

/* card and badge sit above dim */
.vnk-bg-card {
  position: absolute;
  left: 50%;
  bottom: calc(var(--arc-height) + 64px); /* desktop: above arc + thumbs */
  transform: translateX(-50%);
  z-index: 2;
  color: #fff;
  pointer-events: none;
  max-width: 80%;
  text-align: center;
  text-shadow: 0 1px 0 rgba(0,0,0,0.45);
  font-family: inherit;
}
.vnk-bg-title { font-weight: 700; font-size: 20px; margin-bottom:6px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.vnk-bg-prices { font-size:18px; display:flex; gap:20px; align-items:left; justify-content:center; }
.vnk-bg-price { color:#ff5050; font-weight:700; }
.vnk-bg-compare { text-decoration:line-through; opacity:0.78; font-weight:500; font-size:20px; }

.vnk-bg-badge {
  position: absolute;
  z-index: 2;
  background: #ff6b4b;
  color: #fff;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  left: 50%; /* 👈 fix typo */
  bottom: calc(var(--arc-height) + 175px); /* ~40px above card bottom */
  transform: translateX(120%); /* pushes badge to the right of the card */
  pointer-events: none;
  box-shadow: 0 6px 18px rgba(0,0,0,0.18);
}

/* responsive: adjust card above thumbs on mobile */
@media (max-width:640px) {
  .vnk-bg-title { font-size: 24px; }
  .vnk-bg-prices { font-size: 20px; }

  .vnk-bg-card {
    bottom: calc(var(--arc-height) / 2 + 182px); /* mobile: closer to thumbs */
    max-width: 86%;
    padding: 0 8px;
  }
}
    `;
    const s = document.createElement('style');
    s.id = 'vnk-bg-products-styles';
    s.appendChild(document.createTextNode(css));
    document.head.appendChild(s);
  }

  // Build DOM
  function mountCarousel(host, products) {
    if (!host || !products || !products.length) return;

    const prev = host.querySelector('.vnk-bg-carousel');
    if (prev) prev.remove();

    const carousel = document.createElement('div');
    carousel.className = 'vnk-bg-carousel';
    const track = document.createElement('div');
    track.className = 'vnk-bg-carousel-track';
    carousel.appendChild(track);

    products.forEach((p, i) => {
      const s = document.createElement('div');
      s.className = 'vnk-bg-slide';
      const cleanImg = extractRawUrl(p.img || '') || (p.img || '');
      s.style.backgroundImage = cleanImg ? `url("${cleanImg.replace(/(["'])/g,'')}")` : '';
      s.dataset.index = String(i);
      s.dataset.title = p.title || '';
      s.dataset.price = p.price || '';
      s.dataset.compare = p.compare_at || '';
      track.appendChild(s);
    });

    const dim = document.createElement('div');
    dim.className = 'vnk-bg-dim';

    const card = document.createElement('div');
    card.className = 'vnk-bg-card';
    card.innerHTML = `<div class="vnk-bg-title"></div><div class="vnk-bg-prices"><span class="vnk-bg-price"></span><span class="vnk-bg-compare"></span></div>`;

    const badge = document.createElement('div');
    badge.className = 'vnk-bg-badge';
    badge.style.display = 'none';

    carousel.appendChild(dim);
    carousel.appendChild(card);
    carousel.appendChild(badge);

    host.insertBefore(carousel, host.firstChild);

    if (getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }

    startRotation(carousel, products);
  }

  function startRotation(carousel, products) {
    const slides = Array.from(carousel.querySelectorAll('.vnk-bg-slide'));
    if (!slides.length) return;

    const titleEl = carousel.querySelector('.vnk-bg-title');
    const priceEl = carousel.querySelector('.vnk-bg-price');
    const compareEl = carousel.querySelector('.vnk-bg-compare');
    const badgeEl = carousel.querySelector('.vnk-bg-badge');

    let idx = 0;

    function show(i) {
      i = ((i % slides.length) + slides.length) % slides.length;
      slides.forEach((s, j) => {
        if (j === i) s.classList.add('vnk-active');
        else s.classList.remove('vnk-active');
      });

      const active = slides[i];
      const title = active.dataset.title || '';
      const price = active.dataset.price || '';
      const compare = active.dataset.compare || '';

      titleEl.textContent = title;
      if (compare && parseFloat(compare) > parseFloat(price)) {
        priceEl.textContent = formatINR(price);
        compareEl.textContent = formatINR(compare);
        const d = computeDiscount(compare, price);
        badgeEl.textContent = `-${d}%`;
        badgeEl.style.display = '';
      } else {
        priceEl.textContent = formatINR(price);
        compareEl.textContent = '';
        badgeEl.style.display = 'none';
      }
    }

    show(idx);
    setInterval(() => { idx = (idx + 1) % slides.length; show(idx); }, ROTATE_MS);
  }

  async function fetchGraphQLProducts() {
    if (!sfToken) return null;
    try {
      const query = `
        query {
          collectionByHandle(handle: "${COLLECTION_HANDLE}") {
            products(first: ${MAX_PRODUCTS}) {
              nodes {
                title handle
                images(first:1) { nodes { url } }
                variants(first:1) { nodes { price { amount } compareAtPrice { amount } } }
              }
            }
          }
        }`;
      const res = await fetch(gqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': sfToken
        },
        body: JSON.stringify({ query })
      });
      if (!res.ok) throw new Error('GraphQL network error: ' + res.status);
      const j = await res.json();
      if (!j || !j.data || !j.data.collectionByHandle || !j.data.collectionByHandle.products) return null;
      const nodes = j.data.collectionByHandle.products.nodes || [];
      const products = nodes.map(n => {
        const img = (n.images?.nodes?.[0]?.url) || '';
        const v0 = n.variants?.nodes?.[0] || {};
        const price = v0.price?.amount || v0.price || '0';
        const compare_at = v0.compareAtPrice?.amount || v0.compareAtPrice || '';
        return { title: n.title || '', handle: n.handle || '', img, price, compare_at };
      }).filter(p => p.img);
      return products.length ? products : null;
    } catch (e) {
      console.warn('vnk-bg-products: GraphQL failed', e);
      return null;
    }
  }

  async function fetchRestProducts() {
    try {
      const res = await fetch(restEndpoint, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('REST fetch failed: ' + res.status);
      const j = await res.json();
      const products = (j.products || []).map(p => {
        const img = p.images?.[0]?.src || p.images?.[0]?.url || '';
        const v = p.variants?.[0] || {};
        return { title: p.title || '', handle: p.handle || '', img, price: v.price || '0', compare_at: v.compare_at_price || '' };
      }).filter(p => p.img);
      return products.length ? products : null;
    } catch (e) {
      console.warn('vnk-bg-products: REST failed', e);
      return null;
    }
  }

  async function init() {
    if (document.readyState === 'loading') {
      await new Promise(r => document.addEventListener('DOMContentLoaded', r, { once: true }));
    }

    const attemptMount = async (host) => {
      injectStyles();
      let products = await fetchGraphQLProducts();
      if (!products || !products.length) products = await fetchRestProducts();
      window.__vnk_bg_products_debug = products || [];
      if (!products || !products.length) {
        console.warn('vnk-bg-products: no products found for', COLLECTION_HANDLE);
        return;
      }
      mountCarousel(host, products.slice(0, MAX_PRODUCTS));
    };

    const host = document.querySelector('.vnk-panel-bg');
    if (host) { attemptMount(host); return; }

    const mo = new MutationObserver((_, obs) => {
      const h = document.querySelector('.vnk-panel-bg');
      if (h) { obs.disconnect(); attemptMount(h); }
    });
    mo.observe(document.documentElement || document.body, { childList: true, subtree: true });
  }

  init();
})();
